import { Facebook, Instagram, LinkedIn, Twitter } from '@mui/icons-material';
import { Avatar, Box, Container, Grid, IconButton, Link, Paper, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { IPostViewModel } from 'viewModels';
import { ButtonOpenSolicitation, NavHeader, UserMap } from '../../../components';
import { Publication } from '../../../components/FeedPublication/Publication';
import { useApp, useAuth } from '../../../context';
import { Download } from '../../../controller/FileController';
import { UserController } from '../../../controller/UserController';
import { Content } from './styles';
import { useAlert } from '../../../hooks/useAlert';
import { useDistricts } from '../../../context/districts';
import { UF_BY_CODE } from '../../../constants';

const BORDER_RADIUS: number = 4;

// const contactTypes = {
//   [TypeContact.CELL]: 'Celular',
//   [TypeContact.PHONE]: 'Telefone',
//   [TypeContact.EMAIL]: 'E-mail',
// };

//const ignoredContactMask = ['@', '.', '(', ')', '-'];

const PublicFeed: React.FC = () => {
  const theme = useTheme();
  const { districtsSelectedsByUser } = useDistricts();
  const app = useApp();
  const alert = useAlert();
  const navigate = useNavigate();
  const { user } = useAuth();
  const urlFromState = useLocation().state?.url ?? '';
  const urlFromQuery = useLocation().pathname?.split('/')?.pop() ?? '';
  //const [showOnContacts, setShowOnContacts] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string | undefined>(undefined);
  const [posts, setPosts] = useState<IPostViewModel[]>([]);
  const { data, isLoading } = useQuery(
    urlFromQuery || urlFromState,
    () => UserController.getPublicProfile(urlFromQuery || urlFromState),
    {
      refetchOnWindowFocus: false,
      onError: () => {
        alert.show({ message: 'Não foi possível carregar o perfil.', type: 'error' });
        navigate('/#find-correspondents');
      },
      enabled: !!urlFromState || !!urlFromQuery,
    },
  );

  const districtsUfAcronyms = useMemo(() => {
    const districts = districtsSelectedsByUser?.map((d) => UF_BY_CODE[d.ufCode]);
    return districts.filter((d, index) => districts.indexOf(d) === index);
  }, [districtsSelectedsByUser]);

  const districtsUfs = useMemo(() => {
    const districts = districtsSelectedsByUser?.map((d) => d.uf);
    return districts.filter((d, index) => districts.indexOf(d) === index);
  }, [districtsSelectedsByUser]);

  useEffect(() => {
    app.setLoading(isLoading);
  }, [app, isLoading]);

  useEffect(() => {
    if (data && data.profilePictureId) {
      Download(data.profilePictureId).then((response) =>
        setProfilePicture(URL.createObjectURL(response)),
      );
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      setPosts(data?.posts ?? []);
    }
  }, [data]);

  return (
    <Content>
      <NavHeader typeHeader="company" />
      <Container
        sx={{
          my: 0,
          maxHeight: 'calc(100vh - 68px)',
          overflowY: 'auto',
          maxWidth: '1920px !important',
        }}>
        <Grid container component="main" spacing={5} sx={{ my: 0 }}>
          <Grid
            container
            item
            sm={12}
            md={3}
            spacing={1}
            alignContent="flex-start"
            sx={{ pt: '96px !important' }}>
            <Grid item xs={12}>
              <Paper
                sx={{
                  borderRadius: BORDER_RADIUS,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  pb: 2,
                }}>
                <Avatar
                  alt={data?.firstName}
                  src={profilePicture}
                  sx={{
                    width: 160,
                    height: 160,
                    mt: -10,
                    '@media (max-width: 500px)': {
                      width: 100,
                      height: 100,
                      marginTop: -8,
                    },
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    color: theme.colors.secondary,
                    fontSize: '1.5rem',
                    fontWeight: 500,
                    textAlign: 'center',
                    mt: 1,
                  }}>
                  {user?.id === data?.id
                    ? 'Você'
                    : `${data?.firstName ?? ''} ${data?.lastName ?? ''}`}
                </Typography>
                {data?.linkSite && (
                  <Link
                    color="primary"
                    href={data.linkSite}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    sx={{
                      mt: 0.5,
                    }}>
                    {data.linkSite.split('//')?.[1] ?? 'Acessar site'}
                  </Link>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ borderRadius: BORDER_RADIUS, p: 2 }}>
                <Typography
                  variant="h2"
                  sx={{
                    color: theme.colors.secondary,
                    fontSize: '1rem',
                    fontWeight: 500,
                  }}>
                  {data?.address?.city ? `De ${data?.address?.city}` : 'Local não informado'}
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  sx={{
                    textDecoration: 'underline',
                    mb: 2,
                  }}>
                  Onde atua
                </Typography>
                {data?.presentation && (
                  <>
                    <Typography
                      variant="h2"
                      sx={{
                        color: theme.colors.secondary,
                        fontSize: '1rem',
                        fontWeight: 500,
                      }}>
                      Apresentação
                    </Typography>
                    <Typography sx={{ maxHeight: '100px', overflow: 'hidden', overflowY: 'auto' }}>
                      {data?.presentation}
                    </Typography>
                  </>
                )}
                <div className="socialMedias">
                  {data?.linkFacebook && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(data?.linkFacebook, '_blank')}>
                      <Facebook />
                    </IconButton>
                  )}
                  {data?.linkInstagram && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(data?.linkInstagram, '_blank')}>
                      <Instagram />
                    </IconButton>
                  )}
                  {data?.linkTwitter && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(data?.linkTwitter, '_blank')}>
                      <Twitter />
                    </IconButton>
                  )}
                  {data?.linkLinkedIn && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(data?.linkLinkedIn, '_blank')}>
                      <LinkedIn />
                    </IconButton>
                  )}
                </div>
              </Paper>
            </Grid>
            {/* <Grid item xs={12}>
              <ButtonOpenSolicitation />
            </Grid> */}
          </Grid>
          <Grid container item sm={12} md={5} alignContent="flex-start">
            <Grid item xs={12} sx={{ height: '4.5rem' }}>
              <Typography variant="h5" sx={{ color: theme.colors.secondary }}>
                Publicações
              </Typography>
            </Grid>
            <Grid
              container
              item
              xs={12}
              rowSpacing={2}
              sx={{
                overflow: 'hidden',
                overflowY: 'auto',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                maxHeight: 'calc(100vh - 165px)',
                '::-webkit-scrollbar': {
                  width: '0 !important',
                },
                '& > :first-of-type': {
                  paddingTop: '0 !important',
                },
              }}>
              {posts.length === 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ borderRadius: BORDER_RADIUS, p: 2, marginBottom: '0.2rem' }}>
                    Nenhuma postagem encontrada...
                  </Paper>
                </Grid>
              )}
              {posts.map((post) => (
                <Grid key={post.id} item xs={12} sx={{ paddingTop: '0px !important' }}>
                  <Publication key={post.id} post={post} margin="0.3rem 0rem" />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid
            container
            item
            sm={12}
            md={4}
            spacing={1}
            alignContent="flex-start"
            sx={{
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 52px)',
              '::-webkit-scrollbar': {
                width: '0 !important',
              },
            }}>
            {/* <Grid item xs={12}>
              <ButtonBase
                sx={{ width: '100%', borderRadius: BORDER_RADIUS }}
                onClick={() => navigate(tokenIsValid ? '/contacts' : '/login')}>
                <Paper
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    borderRadius: BORDER_RADIUS,
                    p: 1,
                  }}>
                  <PersonAddAlt1 color="primary" sx={{ pr: 1 }} />
                  <Typography variant="h5" color="primary">
                    Adicionar contato
                  </Typography>
                </Paper>
              </ButtonBase>
            </Grid> */}
            <Grid item xs={12}>
              <Grid
                item
                xs={12}
                sx={{
                  height: '3.5rem',
                  '@media (min-width: 900px)': {
                    display: 'none',
                  },
                }}>
                <Typography variant="h5" sx={{ color: theme.colors.secondary }}>
                  Informações
                </Typography>
              </Grid>
              <Paper sx={{ p: 2, borderRadius: BORDER_RADIUS }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.secondary }}>
                  Onde atua?
                </Typography>
                <UserMap districts={districtsUfAcronyms} />
                <Box sx={{ maxHeight: '170px', overflowY: 'auto' }}>
                  {districtsUfs &&
                    districtsUfs.map((uf) => (
                      <Box key={uf} sx={{ mb: 0.5 }}>
                        <Typography
                          color="primary"
                          sx={{
                            fontSize: '1.25rem',
                            textDecoration: 'underline',
                          }}>
                          {uf ?? 'Estado desconhecido'}
                        </Typography>
                        <Typography color="text" sx={{ fontSize: '1rem' }}>
                          {districtsSelectedsByUser
                            .filter((d) => d.uf === uf)
                            .map((district) => (
                              <p key={district.id}>{district.name},</p>
                            ))}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Paper>
            </Grid>
            {!!data?.specialties?.length && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: BORDER_RADIUS }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: theme.colors.secondary }}>
                    Especialidades
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1, maxHeight: '120px', overflowY: 'auto' }}>
                    {data?.specialties?.map((s) => (
                      <Grid key={s.name} item xs={12} sm={6}>
                        <Typography color="primary" sx={{ fontSize: '1rem' }}>
                          {s.name}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            )}
            {!!data?.correspondences?.length && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: BORDER_RADIUS }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: theme.colors.secondary }}>
                    Serviços de correspondência
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1, maxHeight: '120px', overflowY: 'auto' }}>
                    {data?.correspondences?.map((s) => (
                      <Grid key={s.name} item xs={12} sm={6}>
                        <Typography color="primary" sx={{ fontSize: '1rem' }}>
                          {s.name}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            )}
            {/* <Grid item xs={12}>
              <Paper sx={{ p: 2, borderRadius: BORDER_RADIUS, position: 'relative', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.secondary }}>
                  Contatos
                </Typography>
                <Button
                  variant="text"
                  onClick={() =>
                    tokenIsValid ? setShowOnContacts((prev) => !prev) : navigate('/login')
                  }
                  sx={{
                    position: 'absolute',
                    top: '3rem',
                    right: 16,
                    textTransform: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}>
                  {showOnContacts ? 'Esconder' : 'Mostrar'} contatos
                </Button>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {data?.externalContacts?.map((contact) => (
                    <Grid key={contact.value} item xs={12}>
                      <Typography color="text" sx={{ fontSize: '1rem' }}>
                        {contactTypes[contact.type] ?? 'Contato'}:{' '}
                        {`${!contact?.value
                          ? 'Não informado'
                          : tokenIsValid && showOnContacts
                            ? contact.value
                            : `${contact.value
                              ?.split('')
                              ?.map((c, i) => {
                                if (
                                  i >= (contact.type !== TypeContact.EMAIL ? 6 : 3) &&
                                  !ignoredContactMask.includes(c)
                                )
                                  return '*';
                                return c;
                              })
                              .join('')}`
                          }`}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid> */}
          </Grid>
        </Grid>
      </Container>
    </Content>
  );
};

export default PublicFeed;
